import { Suspense, useState } from 'react';

import { Button, Skeleton } from '@iwoca/orion';
import { H1, Text } from '@ui';
import { FormattedMessage } from 'react-intl';

import { useGetDeReferralsProfile } from './useGetDeReferralsProfile';
import { ErrorBoundary } from '../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../modals/fallbackErrorModal/FallbackErrorModal';

export const Referral = () => {
    return (
        <div className="mx-auto min-h-screen max-w-5xl space-y-2xl px-m py-xl min-m:px-l">
            <div className="space-y-s">
                <H1>
                    <FormattedMessage
                        id="_.referral.title"
                        defaultMessage="Kunden-werben-Freunde"
                    />
                </H1>
                <Text>
                    <FormattedMessage
                        id="_.referral.intro"
                        defaultMessage="Refer a friend to iwoca and earn rewards through our Kunden-werben-Freunde programme."
                    />{' '}
                    <a
                        href="https://www.iwoca.de/kunden-werben-freunde"
                        target="_blank"
                        rel="noreferrer"
                        className="underline"
                    >
                        <FormattedMessage
                            id="_.referral.learn_more"
                            defaultMessage="Learn more"
                        />
                    </a>
                </Text>
            </div>

            <ErrorBoundary fallback={() => <FallbackErrorModal />}>
                <Suspense fallback={<ReferralSkeleton />}>
                    <ReferralContent />
                </Suspense>
            </ErrorBoundary>
        </div>
    );
};

const ReferralContent = () => {
    const { data } = useGetDeReferralsProfile();
    const asReferrer = data?.as_referrer;

    if (!asReferrer?.is_eligible) {
        return (
            <Text>
                <FormattedMessage
                    id="_.referral.ineligible"
                    defaultMessage="Unfortunately you are currently not eligible to participate in the Kunden-werben-Freunde programme."
                />
            </Text>
        );
    }

    return (
        <div className="space-y-2xl">
            <Text>
                <FormattedMessage
                    id="_.referral.share_description"
                    defaultMessage="Share your personal link or code with friends and acquaintances — once they successfully take out a loan, both parties are rewarded."
                />
            </Text>

            <ReferralShareTabs
                referralCode={asReferrer.referral_code}
                referralLink={asReferrer.referral_link}
            />

            <TaxFootnote />
        </div>
    );
};

const ReferralShareTabs = ({
    referralCode,
    referralLink
}: {
    referralCode: string;
    referralLink: string;
}) => {
    const [activeTab, setActiveTab] = useState<'code' | 'link'>('link');

    return (
        <div>
            <div className="mb-m flex gap-s">
                <Button
                    variant={activeTab === 'link' ? 'secondary' : 'tertiary'}
                    onClick={() => setActiveTab('link')}
                    data-testid="tab-link"
                >
                    <FormattedMessage
                        id="_.referral.referral_link_label"
                        defaultMessage="Your referral link"
                    />
                </Button>
                <Button
                    variant={activeTab === 'code' ? 'secondary' : 'tertiary'}
                    onClick={() => setActiveTab('code')}
                    data-testid="tab-code"
                >
                    <FormattedMessage
                        id="_.referral.referral_code_label"
                        defaultMessage="Your referral code"
                    />
                </Button>
            </div>
            <div className="rounded-b-s rounded-tr-s bg-structure-100 shadow-black-s">
                <div className="space-y-m p-l">
                    <Text className="pl-xs font-med text-structure-40">
                        {activeTab === 'link' ? (
                            <FormattedMessage
                                id="_.referral.link_instructions"
                                defaultMessage="Share this link with family, friends or acquaintances that iwoca could help."
                            />
                        ) : (
                            <FormattedMessage
                                id="_.referral.code_instructions"
                                defaultMessage="Alternatively, you can simply share this code. It must be entered at the start of the loan application."
                            />
                        )}
                    </Text>
                    {activeTab === 'link' ? (
                        <ReferralLinkCard referralLink={referralLink} />
                    ) : (
                        <ReferralCodeCard referralCode={referralCode} />
                    )}
                </div>
            </div>
        </div>
    );
};

const ReferralCodeCard = ({ referralCode }: { referralCode: string }) => {
    return (
        <div className="flex items-center gap-xl">
            <div className="rounded-s border border-secondary-90 px-l py-m">
                <span
                    className="text-xl font-bold tracking-widest text-primary-10"
                    data-testid="referral-code"
                >
                    {referralCode}
                </span>
            </div>
            <CopyButton
                textToCopy={referralCode}
                data-testid="copy-referral-code"
                copyLabel={
                    <FormattedMessage
                        id="_.referral.copy_code"
                        defaultMessage="Copy code"
                    />
                }
            />
        </div>
    );
};

const ReferralLinkCard = ({ referralLink }: { referralLink: string }) => {
    return (
        <div className="flex flex-wrap items-center gap-m">
            <div className="min-w-0 rounded-s border border-secondary-90 px-l py-m">
                <span
                    className="block break-all text-xl font-bold text-primary-10"
                    data-testid="referral-link"
                >
                    {referralLink}
                </span>
            </div>
            <CopyButton
                textToCopy={referralLink}
                data-testid="copy-referral-link"
                copyLabel={
                    <FormattedMessage
                        id="_.referral.copy_link"
                        defaultMessage="Copy link"
                    />
                }
            />
        </div>
    );
};

const DEFAULT_COPIED_LABEL = (
    <FormattedMessage id="_.referral.code_copied" defaultMessage="Copied!" />
);

const CopyButton = ({
    textToCopy,
    copyLabel,
    copiedLabel = DEFAULT_COPIED_LABEL,
    ...buttonProps
}: {
    textToCopy: string;
    copyLabel: React.ReactNode;
    copiedLabel?: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            // Silently ignore clipboard errors — "Copied!" simply won't show
            .catch(() => {});
    };

    return (
        <Button variant="primary" onClick={handleCopy} {...buttonProps}>
            {copied ? copiedLabel : copyLabel}
        </Button>
    );
};

const TaxFootnote = () => (
    <div className="mt-4xl space-y-m text-xs text-structure-40">
        <p className="m-0">
            <FormattedMessage
                id="_.referral.tax_footnote_intro"
                defaultMessage="<b>Steuerlicher Hinweis:</b> The referral reward is granted as a non-cash benefit (voucher/material reward) worth up to €2,500. Tax treatment is the responsibility of the receiving person or business."
                values={{
                    b: (chunks: React.ReactNode) => <strong>{chunks}</strong>
                }}
            />
        </p>
        <p className="m-0">
            <FormattedMessage
                id="_.referral.tax_footnote_businesses"
                defaultMessage="<b>Receiving businesses</b> (for example, GmbH, UG, sole traders, freelancers) treat the reward value as business income and tax it as part of regular profit determination. <b>Receiving private individuals</b> tax the reward value as other income pursuant to §22 No. 3 EStG as part of their personal income tax return (exemption limit: €256 per calendar year for all income of this type)."
                values={{
                    b: (chunks: React.ReactNode) => <strong>{chunks}</strong>
                }}
            />
        </p>
        <p className="m-0">
            <FormattedMessage
                id="_.referral.tax_footnote_employees"
                defaultMessage="<b>Employees of a referring business</b> who receive the reward personally should clarify the tax treatment (potentially third-party wages per §38(1) sentence 3 EStG) with their employer and tax adviser."
                values={{
                    b: (chunks: React.ReactNode) => <strong>{chunks}</strong>
                }}
            />
        </p>
        <p className="m-0">
            <FormattedMessage
                id="_.referral.tax_footnote_receipt"
                defaultMessage="The receiving person/business receives a payment receipt from iwoca for the reward. This serves solely as proof and <b>does not constitute an invoice or VAT credit note</b>. iwoca does not withhold taxes for recipients and does not provide tax advice. Please consult your tax adviser with any questions."
                values={{
                    b: (chunks: React.ReactNode) => <strong>{chunks}</strong>
                }}
            />
        </p>
    </div>
);

const ReferralSkeleton = () => (
    <div className="space-y-2xl">
        <div className="space-y-m">
            <Skeleton className="h-5 w-full max-w-xl" />
            <Skeleton className="h-5 w-3/4 max-w-xl" />
        </div>
        <div>
            <div className="mb-m flex gap-s">
                <Skeleton className="h-10 w-36" />
                <Skeleton className="h-10 w-36" />
            </div>
            <div className="rounded-b-s rounded-tr-s bg-structure-100 shadow-black-s">
                <div className="space-y-m p-l">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-12 w-72" />
                </div>
            </div>
        </div>
    </div>
);
